/* global VideoDecoder, EncodedVideoChunk */

/**
 * PlayerWS - WebSocket H.264/H.265 Video Player
 *
 * Simplified player that treats each WebSocket binary message as a complete
 * Access Unit (AU). The mpengine backend sends complete AUs, so no NAL
 * aggregation is needed.
 *
 * Based on dividia-api WSPlayer.js approach.
 */
import { debugWarn } from '../../util/debug';
import { drawOverlays } from '../../player/overlayRenderer';
import { getOverlayPrefs, subscribeOverlayPrefs } from '../../common/overlayPrefs';
import { hevcCodecStringFromPtl } from '../../util/hevcCodec';
import { parseB2FromSample, b2ToOverlayMetadata, formatB2Timestamp } from '../../player/b2Sei';
import { readDecoderPref, writeDecoderPref, detectPumping, PUMP_MIN_SAMPLES } from '../../util/decoderPrefs';

class PlayerWS {
	constructor(canvasElement, wsUrl, messageQueue = null, options = {}) {
		// Accept canvas element directly instead of ID
		this.canvas = canvasElement;
		if (!this.canvas) {
			console.error('[PlayerWS] Canvas element not provided');
			return;
		}

		// Generate unique canvas ID if not set
		if (!this.canvas.id) {
			this.canvas.id = `camera-${Math.random().toString(36).substr(2, 9)}`;
		}

		// Standard canvas context (matching working reference implementation)
		this.ctx = this.canvas.getContext("2d");
		this.wsUrl = wsUrl;
		this.reconnectDelay = 5000;
		this.reconnectTimer = null;
		this.shouldReconnect = true;
		this._reconnectAttempts = 0;
		this.lastMessage = performance.now();
		this.lastVideoFrame = performance.now();
		this.lastDecodedFrame = 0;
		this._firstBinaryAt = 0;
		this._videoStalled = false;
		this._decoderStallNotified = false;
		this.stallInterval = null;
		this._reconnectCountdown = null;

		this.metadata = null;
		this.decoder = null;
		this.codec = null;
		this.timestampCounter = 0;

		// Timing for pumping detection
		this.lastWsMessageTime = 0;
		this.lastFrameTime = 0;
		this.frameTimings = [];

		// Lightweight stats for the "Camera Metadata" overlay (#86bb5dx62 L-D3): rolling
		// frame + byte counts since the last getStats() call → FPS + bitrate. Cheap counters
		// in the decode path; the rate is computed only when the overlay polls getStats().
		this._statFrames = 0;
		this._statBytes = 0;
		this._statSince = performance.now();

		// Hardware/Software decoder selection — the SAME mechanism as PlaybackPlayer, sharing
		// util/decoderPrefs: the seed list + a localStorage pref keyed by `dvlite_dec_${codec}_${w}x${h}`
		// (the decode workload), so a preference learned by EITHER player, for the same codec+resolution,
		// is honored by the other. The resolution isn't known until the first decoded frame, so we start
		// on hardware and, in handleFrame:
		//   1. apply the known/seeded preference immediately (_applyDecoderPref), then
		//   2. if the workload is UNKNOWN, run runtime pump-detection (checkForPumping) and persist the
		//      result — for EVERY profile. A pumping hardware decoder is bad UX at any cam count, and
		//      software is engaged ONLY when hardware is demonstrably pumping (then shared + reused, so
		//      the detection cost is paid once per workload, not per cam or per session).
		// `profile` still drives the live JPEG→WS upgrade cadence elsewhere; it no longer gates decode.
		this.profile = options.profile || 1;
		this.useHardwareAccel = true;
		this.pumpingCheckDone = false;
		this._decoderPrefApplied = false;

		this.mq = messageQueue;

		// List of object ids we have seen. So we do not spam events of the same object
		this.objIds = [];

		// Callback options for React integration
		this.onConnectionChange = options.onConnectionChange || null;
		// The server stopped this stream ({ code, message }); see _serverStop.
		this.onServerStop = options.onServerStop || null;
		this.serverError = null;
		this.onMetadataUpdate = options.onMetadataUpdate || null;
		this.onError = options.onError || null;
		this.onFrame = options.onFrame || null;
		// Fired ONCE when the decoder has settled (the pumping check is done: pref adopted, or 20-frame
		// detection decided hw vs sw). The live tile holds the JPEG until this so the ~2s hardware pump
		// window is masked instead of shown on the canvas (#86bbtgfh0).
		this.onSettled = options.onSettled || null;

		// Overlay visibility toggles. Seed from the global overlay prefs (Setup > Display
		// & Theme / in-view control), letting an explicit option override; then subscribe
		// so a live toggle updates this already-streaming player without a reload.
		const prefs = getOverlayPrefs();
		this.showObjects = typeof options.showObjects === "boolean" ? options.showObjects : prefs.objects;
		this.showLpr = typeof options.showLpr === "boolean" ? options.showLpr : prefs.lpr;
		// Motion windows ride the "Camera Info" pref (prefs.metadata), mirroring dview where the
		// info toggle draws the VMD windows. PROVISIONAL mapping — the overlay-gating design pass
		// may split this into its own pref. (No engine vmd on the ws yet; harmless until then.)
		this.showMotion = typeof options.showMotion === "boolean" ? options.showMotion : prefs.metadata;
		this._overlayUnsub = subscribeOverlayPrefs(p => this.setOverlayVisibility({ showObjects: p.objects, showLpr: p.lpr, showMotion: p.metadata }));

		this.connect();
	}

	isObjNew(id) {
		// If id is already in the object list return false
		if (this.objIds.includes(id))
			return false;

		if (this.objIds.length > 100)
			this.objIds.shift();

		this.objIds.push(id);
		return true;
	}

	// Rolling stats for the "Camera Metadata" overlay (#86bb5dx62 L-D3): FPS + bitrate since
	// the last call, plus the live codec + decoded resolution. CameraStreamContainerWs polls
	// this ~1s ONLY while the overlay is visible; each call resets the counting window.
	getStats() {
		const now = performance.now();
		const dt = Math.max(1, now - this._statSince) / 1000; // seconds (guard divide-by-zero)
		const stats = {
			fps: Math.round((this._statFrames / dt) * 10) / 10,
			kbps: Math.round((this._statBytes * 8) / 1000 / dt),
			codec: this.codec || null,
			width: this.canvas ? this.canvas.width : 0,
			height: this.canvas ? this.canvas.height : 0,
		};
		this._statFrames = 0;
		this._statBytes = 0;
		this._statSince = now;
		return stats;
	}

	// codecKind for the shared decoderPrefs key/list: live labels HEVC "h265"; the shared util (and
	// playback) use "hevc". H.265 never goes to software (hardware-only on macOS), so it never reaches
	// the persisted key, but map it so a seed lookup matches.
	_codecKind() { return this.codec === "h265" ? "hevc" : "h264"; }

	// Read the learned/seeded hw↔sw preference for the CURRENT codec + resolution and apply it,
	// switching a hardware decoder to software when the workload is known to pump. Returns true if a
	// known preference was applied (runtime detection can stop), false if the workload is unknown
	// (caller should run detection). Shared with playback via util/decoderPrefs — a pref learned by
	// either player, on the same codec+resolution, is honored here (and vice-versa).
	// Mark the decoder settled (hw/sw chosen) and notify onSettled exactly once. All the places that
	// finish the pumping check route through here so the JPEG→canvas reveal fires on the real settle
	// point, not the first frame (#86bbtgfh0).
	_markSettled() {
		if (this.pumpingCheckDone) return;
		this.pumpingCheckDone = true;
		if (this.onSettled) { try { this.onSettled(); } catch (e) { /* never break decode */ } }
	}

	_applyDecoderPref() {
		// HEVC is hardware-only on macOS Safari/Chrome — never move it off hardware.
		if (this.codec === "h265") { this._markSettled(); return true; }
		const w = this.canvas ? this.canvas.width : 0;
		const h = this.canvas ? this.canvas.height : 0;
		const pref = readDecoderPref(this._codecKind(), w, h);
		if (!pref.known) return false;
		this._markSettled();
		if (!pref.accel && this.useHardwareAccel) {
			debugWarn(`[${this.canvas.id}] software decode (seeded/learned) for h264 ${w}x${h}`);
			this.useHardwareAccel = false;
			this.restartDecoder();
		}
		return true;
	}

	// Detect the hardware-decode "pumping" pattern from the paint cadence and switch to software once,
	// persisting the choice per codec+resolution (the SAME key playback uses). A healthy decoder paints
	// at a steady interval; a pumping one BURSTS. Uses the shared detectPumping thresholds so live and
	// playback never drift. Called from handleFrame while the workload is still undecided.
	checkForPumping() {
		if (this.pumpingCheckDone) return;
		// Chrome on macOS exposes HEVC through its hardware decoder only — never move it to software.
		if (this.codec === "h265") { this._markSettled(); return; }
		if (!this.useHardwareAccel) return;
		// A sibling cam (or the playback player) may have learned/seeded the answer since our first
		// frame — adopt it so only the first cam of a workload pays the detection cost.
		if (this._applyDecoderPref()) return;

		const renderDeltas = this.frameTimings.map(t => t.renderDelta).filter(d => d > 0);
		const pumping = detectPumping(renderDeltas);
		if (pumping === null) return; // < PUMP_MIN_SAMPLES paints — undecided, sample more
		this._markSettled();
		writeDecoderPref(this._codecKind(), this.canvas ? this.canvas.width : 0, this.canvas ? this.canvas.height : 0, pumping);
		if (pumping) {
			debugWarn(`[${this.canvas.id}] Detected pumping, switching to software decoding`);
			this.useHardwareAccel = false;
			this.restartDecoder();
		}
	}

	// Restart decoder with new acceleration setting
	restartDecoder() {
		if (this.decoder && this.decoder.state !== 'closed') {
			this.decoder.close();
		}
		this.decoder = null;
		this.timestampCounter = 0;
		this.frameTimings = [];
		// Decoder will be recreated on next keyframe
	}

	// Notify React components of status changes
	notifyConnectionChange(status, metadata = null) {
		if (this.onConnectionChange) {
			this.onConnectionChange(status, metadata);
		}
	}

	notifyError(error) {
		if (this.onError) {
			this.onError(error);
		}
	}

	/**
	 * Build the WebCodecs codec string for an H.264 stream from its in-band SPS,
	 * e.g. "avc1.4D402A" (Main@L4.2) or "avc1.640028" (High@L4.0). The three bytes
	 * after the SPS (NAL type 7) header ARE the avc1.PPCCLL fields: profile_idc,
	 * constraint-set flags, level_idc.
	 *
	 * WHY this matters: configuring VideoDecoder with a codec string whose profile
	 * doesn't match the bitstream is a spec violation. Chrome's *hardware* decoder
	 * tolerates it, but the *software* decoder rejects High-profile (and often Main)
	 * content with "Decoding error" → black video. These cameras stream
	 * Baseline/Main/High, and the software path is reached both by High-profile
	 * cameras and by the checkForPumping() auto-switch, so the old hardcoded
	 * Baseline string (avc1.42C01E) broke every non-Baseline camera the moment
	 * software decode kicked in. Returns null if no SPS is present (keyframe AUs
	 * here always carry an in-band SPS; caller keeps its default if not).
	 */
	h264CodecString(bytes) {
		if (!bytes) return null;
		const idxs = this.nalHeaderIndices(bytes);
		for (const i of idxs) {
			if ((bytes[i] & 0x1f) === 7 && i + 3 < bytes.length) {
				const hex = x => x.toString(16).padStart(2, "0");
				return `avc1.${hex(bytes[i + 1])}${hex(bytes[i + 2])}${hex(bytes[i + 3])}`;
			}
		}
		return null;
	}

	/**
	 * Copy RBSP bytes starting at `start`, removing HEVC/H.264 emulation-prevention
	 * bytes (a 0x03 emitted after two 0x00). Bounded to `count` output bytes and
	 * stops at the next Annex-B start code, so we never scan the whole AU.
	 *
	 * The HEVC profile_tier_level's compatibility/constraint fields routinely carry
	 * 00 00 00 runs (the captured stream uses 0x60000000), which the encoder escapes on the
	 * wire, so the SPS fields can only be read after un-escaping. H.264 skips this
	 * because its 3 profile bytes almost never form an escape sequence.
	 */
	unescapeRbsp(bytes, start, count) {
		const out = [];
		let zeros = 0;
		for (let i = start; i < bytes.length && out.length < count; i++) {
			const b = bytes[i];
			if (zeros >= 2 && b === 0x03) { zeros = 0; continue; } // drop emulation byte
			if (zeros >= 2 && b === 0x01) break;                   // next NAL's start code
			out.push(b);
			zeros = b === 0x00 ? zeros + 1 : 0;
		}
		return new Uint8Array(out);
	}

	/**
	 * Build the WebCodecs codec string for an H.265/HEVC stream from its in-band
	 * SPS (NAL type 33), e.g. "hvc1.1.6.L150.90" (Main, compat 6, Level 5.0). The
	 * SPS profile_tier_level()'s first 12 bytes are byte-for-byte the hvcC box's
	 * general_* fields (desc[1..12]) that the MP4 playback path parses via the
	 * shared hevcCodecStringFromPtl(), preceded by one byte of
	 * sps_video_parameter_set_id/max_sub_layers/nesting.
	 *
	 * The live path has no hvcC descriptor because it receives raw Annex-B AUs.
	 * Derive the codec string from the SPS, as h264CodecString() does above, so the
	 * decoder configuration describes the bitstream exactly. Live testing showed
	 * Chrome also accepted the captured stream with the old hardcoded string, so this mismatch was
	 * not the JPG-loop root cause. Returns null if no SPS is present. The caller then
	 * keeps its default; keyframe AUs normally carry an in-band SPS.
	 */
	hevcCodecString(bytes) {
		if (!bytes) return null;
		const idxs = this.nalHeaderIndices(bytes);
		for (const i of idxs) {
			if (((bytes[i] >> 1) & 0x3f) !== 33) continue; // HEVC SPS NAL type = 33
			// RBSP starts after the 2-byte NAL header. Need 1 (vps-id) + 12 (PTL) bytes.
			const rbsp = this.unescapeRbsp(bytes, i + 2, 16);
			if (rbsp.length < 13) return null;
			// rbsp[1..12] == hvcC desc[1..12] (the general profile_tier_level).
			return hevcCodecStringFromPtl(rbsp.subarray(1, 13));
		}
		return null;
	}

	initDecoder(codec, keyframeBytes) {
		this.decoder = new VideoDecoder({
			output: frame => this.handleFrame(frame),
			error: e => {
				debugWarn(`[${this.canvas.id}] Decoder error:`, e);
				this.notifyError(e);
			}
		});

		// The stored preference is learned from H.264 pumping. Let the browser pick
		// its normal decoder unless that preference explicitly selected software.
		// This keeps HEVC off the unsupported software-only path without requiring
		// hardware on browsers where an explicit prefer-hardware config is invalid.
		const accel = codec === "h265"
			? "no-preference"
			: (this.useHardwareAccel ? "no-preference" : "prefer-software");

		if (codec === "h264") {
			// Honest codec string from the keyframe's SPS; fall back to Baseline
			// only if no SPS is present.
			const codecString = this.h264CodecString(keyframeBytes) || "avc1.42C01E";
			this.decoder.configure({
				codec: codecString,
				avc: { format: "annexb" },
				hardwareAcceleration: accel
			});
			return;
		}

		if (codec === "h265") {
			// Honest codec string from the keyframe's SPS profile_tier_level; fall
			// back to a Main default only if no SPS is present. Annex-B (in-band
			// VPS/SPS/PPS, no description), mirroring the H264 branch above.
			const codecString = this.hevcCodecString(keyframeBytes) || "hvc1.1.6.L120.90";
			this.decoder.configure({
				codec: codecString,
				hevc: { format: "annexb" },
				hardwareAcceleration: accel
			});
			return;
		}

		debugWarn(`[${this.canvas.id}] Unknown codec requested: ${codec}`);
	}

	stopDecoder() {
		try {
			if (this.decoder && this.decoder.state !== "closed") {
				this.decoder.close();
			}
		} catch (_) { }
		this.decoder = null;
		this.codec = null;
		this.timestampCounter = 0;
	}

	checkStall() {
		const now = performance.now();
		const videoGap = now - this.lastVideoFrame;
		const decodeGap = this.lastDecodedFrame > 0
			? (now - this.lastDecodedFrame)
			: (this._firstBinaryAt > 0 ? (now - this._firstBinaryAt) : 0);

		const stalled = videoGap > 5000 || (decodeGap > 5000 && !this._videoStalled);

		if (stalled && !this._videoStalled) {
			this._videoStalled = true;
			const reason = videoGap > 5000 ? 'no data' : 'no decoded frames';
			debugWarn(`[${this.canvas.id}] Connection stalled (${reason})`);
			this.notifyConnectionChange('stalled');
			if (decodeGap > 5000 && videoGap <= 5000) {
				this.restartDecoder();
			}
		}

		if (this._videoStalled && this.lastDecodedFrame > 0 && decodeGap < 2000) {
			this._videoStalled = false;
			debugWarn(`[${this.canvas.id}] Stream recovered`);
			this.notifyConnectionChange('connected', this.metadata);
		}

		if ((videoGap > 10000 || decodeGap > 10000) && this.ws && this.ws.readyState === WebSocket.OPEN) {
			debugWarn(`[${this.canvas.id}] Stall persists — reconnecting`);
			clearInterval(this.stallInterval);
			this.stallInterval = null;
			this.shouldReconnect = true;
			this.ws.close();
		}
	}

	connect(reconnect) {
		this.serverError = null;
		this.notifyConnectionChange('connecting');
		if (reconnect) {
			this.shouldReconnect = true;
		}

		this.ws = new WebSocket(this.wsUrl);
		this.ws.binaryType = "arraybuffer";

		this.ws.onerror = (err) => {
			console.error(`[${this.canvas.id}] WS error:`, err);
			this.notifyError(err);
		};

		this.ws.onopen = () => {
			this.lastMessage = performance.now();
			this.lastVideoFrame = performance.now();
			this.lastDecodedFrame = 0;
			this._firstBinaryAt = 0;
			this._decoderStallNotified = false;
			this.stallInterval = setInterval(() => this.checkStall(), 1000);
			if (!this._videoStalled) {
				this.notifyConnectionChange('connected');
			}
		};

		this.ws.onclose = (event) => {
			// A refusal or revocation (the server's {type:'error'} frame, then close 1008) is final:
			// reconnecting with the same URL cannot succeed, so the stream stops and says why.
			if (this.serverError || (event && event.code === 1008)) this.shouldReconnect = false;
			if (!this.serverError) this.notifyConnectionChange('disconnected');
			this.stopDecoder();
			clearInterval(this.stallInterval);
			this.stallInterval = null;
			clearInterval(this._reconnectCountdown);
			this._reconnectCountdown = null;
			clearTimeout(this.reconnectTimer);

			if (this.shouldReconnect) {
				this._reconnectAttempts++;
				const delay = Math.min(this.reconnectDelay * Math.pow(2, Math.min(this._reconnectAttempts - 1, 3)), 30000);
				let remaining = Math.ceil(delay / 1000);
				this.notifyConnectionChange('reconnecting', { delay: remaining });
				this._reconnectCountdown = setInterval(() => {
					remaining--;
					if (remaining > 0) {
						this.notifyConnectionChange('reconnecting', { delay: remaining });
					} else {
						clearInterval(this._reconnectCountdown);
						this._reconnectCountdown = null;
					}
				}, 1000);
				this.reconnectTimer = setTimeout(() => {
					clearInterval(this._reconnectCountdown);
					this._reconnectCountdown = null;
					this.connect();
				}, delay);
			}
		};

		this.ws.onmessage = (ev) => {
			this.lastMessage = performance.now();

			if (!this.stallInterval) {
				this.stallInterval = setInterval(() => this.checkStall(), 1000);
			}

			try {
				if (typeof ev.data === "string") {
					const message = JSON.parse(ev.data);
					if (message && message.type === 'error') {
						this._serverStop(message);
						return;
					}
					this.metadata = message;
					this.notifyConnectionChange('connected', this.metadata);
					if (this.onMetadataUpdate) {
						this.onMetadataUpdate(this.metadata);
					}
				} else {
					this.lastVideoFrame = performance.now();
					if (!this._firstBinaryAt) this._firstBinaryAt = performance.now();
					this._reconnectAttempts = 0;
					this.decodeAU(new Uint8Array(ev.data));
				}
			} catch (e) {
				console.error(`[${this.canvas.id}] WS message error:`, e);
				this.notifyError(e);
			}
		};
	}

	/**
	 * Get NAL header offset (after start code) of the FIRST NAL.
	 */
	nalHeaderIndex(bytes) {
		if (bytes[0] === 0x00 && bytes[1] === 0x00 && bytes[2] === 0x01) return 3;
		if (bytes[0] === 0x00 && bytes[1] === 0x00 && bytes[2] === 0x00 && bytes[3] === 0x01) return 4;
		return 0;
	}

	/**
	 * Byte offsets of EVERY NAL header (the byte after each Annex-B start code)
	 * in an access unit. Handles both 3-byte (00 00 01) and 4-byte (00 00 00 01)
	 * start codes. Needed because cloud/dragon AUs are multi-NAL (SEI + SPS + PPS
	 * + slice) and the picture type isn't decided by the first NAL alone.
	 */
	nalHeaderIndices(bytes) {
		const idx = [];
		const n = bytes.length;
		for (let i = 0; i + 2 < n; ) {
			if (bytes[i] === 0x00 && bytes[i + 1] === 0x00 && bytes[i + 2] === 0x01) {
				idx.push(i + 3);
				i += 3;
			} else if (i + 3 < n && bytes[i] === 0x00 && bytes[i + 1] === 0x00 && bytes[i + 2] === 0x00 && bytes[i + 3] === 0x01) {
				idx.push(i + 4);
				i += 4;
			} else {
				i++;
			}
		}
		return idx;
	}

	/**
	 * Detect codec (h264/h265) from an unambiguous SPS or PPS NAL. A slice alone
	 * cannot distinguish the codecs, so callers retry detection on later AUs.
	 */
	detectCodec(bytes) {
		const idxs = this.nalHeaderIndices(bytes);
		// Classify from an unambiguous parameter-set NAL: an HEVC SPS (type 33, read
		// as (byte>>1)&0x3F) or an H.264 SPS/PPS (7/8, read as byte&0x1F). A keyframe
		// AU, the only thing decoding can start from, always carries one, so this
		// is both reliable and sufficient. HEVC SPS is byte 0x42/0x43, which reads as
		// the effectively-unused H.264 slice-data-partition type 2, so it does not
		// collide with real H.264 streams.
		//
		// WHY this matters: the old detector used only the H.264 mask (byte&0x1F) and
		// skipped only the H.264 SEI (6). The captured HEVC substream leads every
		// keyframe AU with an HEVC PREFIX_SEI (type 39 = 0x4E, &0x1F = 14), which is
		// not 6, so it fell through to "(14 in 1..23) ? h264 : h265" → "h264". The
		// stream was then treated as H.264: detectKey never matched, initDecoder was
		// never called, the whole H265 path never ran, and the tile stayed in JPG
		// forever. (Verified live: the WebSocket delivered frames but the player
		// issued zero HEVC VideoDecoder.configure calls.)
		for (const i of idxs) {
			if (((bytes[i] >> 1) & 0x3F) === 33) return "h265"; // HEVC SPS
			const t = bytes[i] & 0x1F;
			if (t === 7 || t === 8) return "h264";              // H.264 SPS / PPS
		}
		// No parameter set in this AU. A lone slice cannot be classified reliably.
		// An HEVC delta (e.g. TRAIL, byte 0x02) and an H.264 P-slice both read as low
		// type numbers under the other codec's mask, and a delta cannot be decoded
		// without its keyframe anyway. Return null so decodeAU DEFERS: it drops this
		// AU and re-detects on the next one, latching the codec only when a
		// parameter-set-bearing keyframe arrives. This is what lets an HEVC camera
		// survive a mid-GOP connect or a reconnect (which nulls this.codec) instead
		// of latching to "h264" off a leading PREFIX_SEI + delta and looping in JPG.
		return null;
	}

	/**
	 * Is this AU a keyframe? Scans ALL NAL units, not just the first.
	 *
	 * Cloud/dragon streams prepend a NON-STANDARD metadata SEI (type 6, "user data
	 * unregistered" — camname/objects/LPR) to EVERY access unit, so a keyframe AU
	 * is SEI → SPS → PPS → IDR. Inspecting only the first NAL (the SEI) would never
	 * see the IDR/SPS/PPS, so the decoder would never initialize (decodeAU drops
	 * every non-key AU until a keyframe) → permanently black video. Local mpengine
	 * sends metadata as separate JSON text frames, so its AUs are param/IDR-led and
	 * decoded either way — this scan is transparent to it.
	 */
	detectKey(bytes, codec) {
		const idxs = this.nalHeaderIndices(bytes);
		for (let k = 0; k < idxs.length; k++) {
			const b = bytes[idxs[k]];
			if (codec === "h264") {
				const t = b & 0x1F;
				if (t === 5 || t === 7 || t === 8) return true; // IDR / SPS / PPS
				if (t === 1) return false; // non-IDR slice → inter frame (params precede it in a key AU)
			} else {
				const t = (b >> 1) & 0x3F;
				if (t === 19 || t === 20 || t === 21 || t === 32 || t === 33 || t === 34) return true;
			}
		}
		return false;
	}

	/**
	 * Extract B2 SEI metadata from cloud/engine_backend AnnexB access units.
	 * Reuses the playback b2Sei parser (same binary struct, different transport).
	 * Fires onMetadataUpdate with the same shape the JSON text-frame path uses
	 * so CameraInfoOverlay picks up camname + timestamp on cloud tiles.
	 */
	extractSeiMetadata(bytes, codec) {
		const seiCodec = codec === 'h265' ? 'hevc' : codec;
		const parsed = parseB2FromSample(bytes, seiCodec, { rbspUnescape: true });
		if (!parsed) return;
		const md = b2ToOverlayMetadata(parsed);
		if (!md) return;
		const meta = {
			camname: md.camname,
			time: formatB2Timestamp(md.time, md.gmtoffset),
			gmtoffset: md.gmtoffset,
		};
		if (md.objects) meta.objects = md.objects;
		if (md.lpr) meta.lpr = md.lpr;
		this.metadata = meta;
		this.notifyConnectionChange('connected', this.metadata);
		if (this.onMetadataUpdate) {
			this.onMetadataUpdate(this.metadata);
		}
	}

	/**
	 * Prepare an access unit for VideoDecoder: trim any leading non-VCL NALs and
	 * reject AUs that carry no coded picture at all. Returns the buffer to decode,
	 * or null to skip the AU.
	 *
	 * WHY (both observed against prod cloud streams):
	 *  - Cloud/dragon prepend a non-standard filler SEI to every AU. Chrome's
	 *    HARDWARE decoder tolerates a leading SEI, but the SOFTWARE decoder rejects
	 *    it ("Decoding error" / flush error). Since checkForPumping() can switch a
	 *    camera to software mid-stream, that leading SEI silently broke playback the
	 *    moment software decode kicked in (and re-poisoned every later camera via
	 *    the stored software preference — the "cascade"). Trimming to the first
	 *    parameter-set/slice NAL makes both decoders happy.
	 *  - engine_backend emits zero-length / SEI-only keepalive AUs when a device's
	 *    live push is idle or degraded (verified: some devices send one keyframe
	 *    then only empty AUs). Feeding a picture-less chunk throws "Null or empty
	 *    decoder buffer", which closed the decoder on device switch → black. We skip
	 *    those instead.
	 *
	 * Local mpengine AUs are already parameter/slice-led and never empty, so this is
	 * a no-op for them (returns the buffer unchanged).
	 *
	 * @returns {Uint8Array|null} the AU to decode, or null to skip it
	 */
	prepareAU(bytes, codec) {
		const idxs = this.nalHeaderIndices(bytes);
		let start = -1;
		let hasSlice = false;
		for (const i of idxs) {
			const t = codec === "h265" ? ((bytes[i] >> 1) & 0x3F) : (bytes[i] & 0x1F);
			const isVcl = codec === "h265" ? (t <= 31) : (t >= 1 && t <= 5);
			const isParam = codec === "h265" ? (t >= 32 && t <= 34) : (t === 7 || t === 8);
			if (isVcl) hasSlice = true;
			if (start < 0 && (isVcl || isParam)) {
				// Back up over this NAL's Annex-B start code (4-byte or 3-byte).
				start = (i >= 4 && bytes[i - 4] === 0 && bytes[i - 3] === 0 && bytes[i - 2] === 0 && bytes[i - 1] === 1) ? i - 4 : i - 3;
			}
		}
		if (!hasSlice) return null; // no coded picture (empty / SEI-only) — skip
		return start > 0 ? bytes.subarray(start) : bytes;
	}

	/**
	 * Decode an Access Unit (complete frame)
	 */
	decodeAU(bytes) {
		const wsArriveTime = performance.now();
		const wsDelta = this.lastWsMessageTime ? (wsArriveTime - this.lastWsMessageTime) : 0;
		this.lastWsMessageTime = wsArriveTime;

		// Empty keepalive AU (engine_backend sends zero-length frames on an idle or
		// degraded push) — nothing to decode, and detecting a codec off it would be
		// bogus. Skip before touching this.codec.
		if (!bytes || bytes.length === 0) return;

		this._statBytes += bytes.length;   // Camera Metadata overlay bitrate (#86bb5dx62)

		// Detect codec once, but only latch it from a parameter-set-bearing AU.
		// detectCodec returns null for a lone slice (mid-GOP / reconnect), so we
		// defer and drop the AU until a keyframe carrying VPS/SPS/PPS arrives.
		// Otherwise an HEVC delta could be misread and latched as h264 forever.
		if (!this.codec) {
			this.codec = this.detectCodec(bytes);
			if (!this.codec) return;
		}

		this.extractSeiMetadata(bytes, this.codec);

		const isKey = this.detectKey(bytes, this.codec);

		// Trim leading junk SEI and drop picture-less AUs before the decoder sees
		// them (see prepareAU). A null result means this AU has no coded picture.
		const au = this.prepareAU(bytes, this.codec);
		if (!au) return;

		// Store timing for pumping detection
		this.currentFrameTiming = {
			wsArrive: wsArriveTime,
			wsDelta: wsDelta
		};

		// Initialize decoder on first keyframe
		if (!this.decoder || this.decoder.state === "closed") {
			if (isKey) {
				this.initDecoder(this.codec, au);
			} else {
				// Drop delta frames until we get a keyframe
				return;
			}
		}

		try {
			this.decoder.decode(new EncodedVideoChunk({
				type: isKey ? "key" : "delta",
				timestamp: this.timestampCounter++,
				data: au
			}));
		} catch (e) {
			console.error(`[${this.canvas.id}] Decode error:`, e);
			this.notifyError(e);
		}
	}

	async cropObjectJPEG(px, py, pw, ph) {
		const tempCanvas = document.createElement("canvas");
		tempCanvas.width = pw;
		tempCanvas.height = ph;

		const tctx = tempCanvas.getContext("2d");
		tctx.drawImage(this.canvas, px, py, pw, ph, 0, 0, pw, ph);

		return new Promise((resolve) => {
			tempCanvas.toBlob((blob) => {
				if (!blob) return resolve(null);

				const reader = new FileReader();
				reader.onloadend = () => resolve(reader.result);
				reader.readAsDataURL(blob);
			}, "image/jpeg", 0.85);
		});
	}

	// Publish newly-seen object detections to the message queue. This is a
	// PLAYER side-effect (thumbnail crop + event) — the shared overlayRenderer
	// does no event work. Called on the CLEAN frame, before overlays are drawn,
	// so cropped thumbnails never include overlay boxes.
	publishObjectEvents() {
		if (!this.mq || !this.metadata?.objects) return;
		for (const obj of this.metadata.objects) {
			if (!obj || !Array.isArray(obj.bbox) || obj.bbox.length !== 4) continue;
			if (!this.isObjNew(obj.id)) continue;
			const [x, y, w, h] = obj.bbox;
			const px = x * this.canvas.width;
			const py = y * this.canvas.height;
			const pw = w * this.canvas.width;
			const ph = h * this.canvas.height;
			(async () => {
				const image = await this.cropObjectJPEG(px, py, pw, ph);
				this.mq.publish("objectEvent", { id: obj.id, label: obj.label, image });
			})();
		}
	}

	// Toggle overlay layers at runtime (UI wiring lands in a later W2 step).
	setOverlayVisibility({ showObjects, showLpr, showMotion } = {}) {
		if (typeof showObjects === "boolean") this.showObjects = showObjects;
		if (typeof showLpr === "boolean") this.showLpr = showLpr;
		if (typeof showMotion === "boolean") this.showMotion = showMotion;
	}

	handleFrame(frame) {
		try {
		const renderTime = performance.now();
		const renderDelta = this.lastFrameTime ? (renderTime - this.lastFrameTime) : 0;
		this.lastFrameTime = renderTime;

		// Calculate decode delay (time from WS message to render)
		const decodeDelay = this.currentFrameTiming ? (renderTime - this.currentFrameTiming.wsArrive) : 0;

		// Store complete timing
		if (this.currentFrameTiming) {
			this.frameTimings.push({
				...this.currentFrameTiming,
				renderTime: renderTime,
				renderDelta: renderDelta,
				decodeDelay: decodeDelay
			});
			// Keep only last 200 samples
			if (this.frameTimings.length > 200) {
				this.frameTimings.shift();
			}
		}

		// Auto-size canvas to match video resolution
		this.canvas.width = frame.displayWidth || frame.codedWidth;
		this.canvas.height = frame.displayHeight || frame.codedHeight;

		// Resolution is known only now (first decoded frame), so this is the earliest the decoder
		// preference — keyed on codec+resolution — can be resolved. Apply the known/seeded hw↔sw choice
		// once; then, while still undecided, sample the paint cadence and auto-switch on the pumping
		// pattern, persisting the result — the same detect-and-store mechanism playback uses.
		if (!this._decoderPrefApplied) {
			this._decoderPrefApplied = true;
			this._applyDecoderPref();
		}
		if (!this.pumpingCheckDone && this.frameTimings.length >= PUMP_MIN_SAMPLES) {
			this.checkForPumping();
		}

		this._statFrames++;   // Camera Metadata overlay FPS (#86bb5dx62)
		this.lastDecodedFrame = performance.now();
		this._decoderStallNotified = false;

		// Draw immediately - no requestAnimationFrame buffering
		// This matches the working reference implementation
		this.ctx.drawImage(frame, 0, 0, this.canvas.width, this.canvas.height);

		// Notify that a frame was successfully decoded and rendered
		if (this.onFrame) {
			this.onFrame();
		}

		// Publish object-detection events BEFORE drawing overlays, so the cropped
		// thumbnails capture the clean frame (no overlay boxes baked in).
		this.publishObjectEvents();

		// Draw all metadata overlays (objects + LPR ROI/plates) through the shared
		// renderer, so live / playback / export-with-overlay all draw identically.
		drawOverlays(this.ctx, this.canvas.width, this.canvas.height, this.metadata, {
			showObjects: this.showObjects,
			showLpr: this.showLpr,
			showMotion: this.showMotion,
		});
		} finally {
			// Always release the decoded VideoFrame — a throw in drawImage/drawOverlays (metadata is
			// network-sourced) would otherwise leak it and exhaust the WebCodecs pool → silent black video.
			frame.close();
		}
	}

	// The cloud proxy refused or ended the stream: {type:'error', code, error}, then close 1008
	// (cloudapi ADR-026; the dividia-api WSPlayer v2.6.0 handles it the same way). Stop for good,
	// report 'failed' with the reason, and let the owner react (a fresh token can help; a retry
	// with this URL cannot). Before this, the frame was read as metadata and the socket reconnected
	// forever.
	_serverStop(message) {
		this.serverError = {
			code: String(message.code || 'error'),
			message: String(message.error || 'The stream was stopped.'),
		};
		this.shouldReconnect = false;
		clearTimeout(this.reconnectTimer);
		this.notifyConnectionChange('failed', this.serverError);
		if (this.onServerStop) this.onServerStop(this.serverError);
	}

	stop() {
		this.shouldReconnect = false;
		this._reconnectAttempts = 0;
		clearInterval(this.stallInterval);
		this.stallInterval = null;
		clearTimeout(this.reconnectTimer);
		clearInterval(this._reconnectCountdown);
		this._reconnectCountdown = null;

		if (this._overlayUnsub) { this._overlayUnsub(); this._overlayUnsub = null; }

		// Clean up websocket and remove event handlers
		if (this.ws) {
			try {
				this.ws.onclose = null;
				this.ws.onerror = null;
				this.ws.onmessage = null;
				this.ws.onopen = null;
				this.ws.close();
			} catch (_) { }
			this.ws = null;
		}

		this.stopDecoder();
		this.notifyConnectionChange('stopped');
	}
}

// What a tile says for a server stop code: plain words, never the server's raw text.
export const serverStopText = (code) => ({
	device_not_authorized: 'Not available to your account',
	token_expired: 'Session expired',
	invalid_token: 'Sign in again',
	video_access_revoked: 'Access removed',
	site_access_revoked: 'Access removed',
	api_key_revoked: 'Access removed',
	camera_not_allowed: 'Not available to your account',   // the lite proxy (cloudapi #86bc7maf2)
	session_ended: 'Sign in again',
}[code] || null);

export default PlayerWS;
